<script setup lang="ts">
/**
 * Social wall app preview, laid out like the players (player-web render/social-wall.ts, Android SocialWallView.kt):
 * a wall of post cards paging through, one big post at a time, or a strip scrolling sideways. Thumbnails (`live` off)
 * hold the first page still.
 */
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { socialGrid, socialLabels, socialPerPage, socialPostList, type DataFeed, type SocialPost, type SocialWallWidget } from '@codot/contracts';
import { loadFeed } from './ChartPreview.vue';
import SocialPostCard from './SocialPostCard.vue';

const props = withDefaults(defineProps<{ config: SocialWallWidget; live?: boolean }>(), { live: true });
const { t } = useI18n();
const feed = shallowRef<DataFeed | null>(null);
const failed = ref(false);
watch(
  () => props.config.feedId,
  async (id) => {
    feed.value = null;
    failed.value = false;
    if (!id) return;
    try {
      feed.value = await loadFeed(id);
    } catch {
      failed.value = true;
    }
  },
  { immediate: true },
);
const posts = computed<SocialPost[]>(() => (feed.value?.table ? socialPostList(feed.value.table, props.config) : []));
const ar = computed(() => props.config.language === 'ar');
const per = computed(() => socialPerPage(props.config));
const pages = computed(() => (props.config.style === 'strip' ? 1 : Math.max(1, Math.ceil(posts.value.length / per.value))));
const page = ref(0);
const now = ref(Date.now());
let timer = 0;
watch(
  () => [props.live, props.config.slideSeconds, pages.value] as const,
  ([live, secs, n]) => {
    clearInterval(timer);
    page.value = 0;
    if (live && n > 1) timer = window.setInterval(() => ((page.value = (page.value + 1) % pages.value), (now.value = Date.now())), secs * 1000);
  },
  { immediate: true },
);
onBeforeUnmount(() => clearInterval(timer));
const slice = computed(() => posts.value.slice(page.value * per.value, page.value * per.value + per.value));
const grid = computed(() => socialGrid(props.config, slice.value.length));
const wallFont = computed(() => `min(${(6.4 / grid.value.rows).toFixed(2)}cqh, ${(5 / grid.value.cols).toFixed(2)}cqw)`);
const stripCols = computed(() => Math.max(1, Math.min(props.config.columns, posts.value.length)));
const stripMoving = computed(() => props.live && posts.value.length > stripCols.value);
const stripPosts = computed(() => (stripMoving.value ? [...posts.value, ...posts.value] : posts.value));
const stripFont = computed(() => `min(5.4cqh, ${(4.6 / stripCols.value).toFixed(2)}cqw)`);
/** A spotlight picture whose link failed: the post shows without it. */
const broken = ref(new Set<string>());
const fail = (url: string) => (broken.value = new Set(broken.value).add(url));
const ok = (url: string | null): url is string => !!url && !broken.value.has(url);
const message = computed(() =>
  !props.config.feedId ? t('appsCat.swPickFeed') : failed.value ? t('appsCat.chartNoFeed') : feed.value && !feed.value.table ? t('appsCat.chartNoTable') : feed.value ? socialLabels(props.config.language).none : '–',
);
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden [container-type:size]" :dir="ar ? 'rtl' : 'ltr'" :style="{ background: config.background, color: config.foreground, padding: 'min(4cqh, 4cqw)' }">
    <p v-if="config.title" class="shrink-0 truncate font-bold" style="font-size: min(8cqh, 5.5cqw); margin-bottom: min(3cqh, 3cqw)">{{ config.title }}</p>
    <div class="relative min-h-0 flex-1 overflow-hidden">
      <p v-if="!posts.length" class="absolute inset-0 flex items-center justify-center text-center opacity-70" style="font-size: min(7cqh, 5cqw)">{{ message }}</p>
      <!-- Strip -->
      <div
        v-else-if="config.style === 'strip'"
        class="absolute inset-y-0 flex"
        :class="[ar ? 'right-0' : 'left-0', stripMoving ? (ar ? 'sw-rtl' : 'sw-ltr') : '']"
        :style="{ animationDuration: `${config.slideSeconds * posts.length}s` }"
      >
        <div v-for="(p, i) in stripPosts" :key="i" class="flex h-full shrink-0" :style="{ width: `calc((100cqw - 2 * min(4cqh, 4cqw)) / ${stripCols})`, padding: '0 min(1.2cqh, 1cqw)' }">
          <SocialPostCard :post="p" :config="config" :font="stripFont" :now="now" class="flex-1" />
        </div>
      </div>
      <Transition v-else name="sw">
        <!-- Spotlight -->
        <div v-if="config.style === 'spotlight'" :key="`s${page}`" class="absolute inset-0 flex items-stretch" style="gap: min(4cqh, 3cqw)">
          <div v-if="ok(slice[0]!.image)" class="w-[45%] shrink-0 overflow-hidden" style="border-radius: min(2cqh, 2cqw)">
            <img :src="slice[0]!.image" alt="" class="h-full w-full object-cover" @error="fail(slice[0]!.image)" />
          </div>
          <SocialPostCard :post="{ ...slice[0]!, image: null }" :config="config" font="min(5.5cqh, 3.2cqw)" :now="now" big class="flex-1" />
        </div>
        <!-- Wall -->
        <div
          v-else
          :key="`w${page}`"
          class="absolute inset-0 grid"
          :style="{ gridTemplateColumns: `repeat(${grid.cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${grid.rows}, minmax(0, 1fr))`, gap: 'min(2.5cqh, 2cqw)' }"
        >
          <SocialPostCard v-for="(p, i) in slice" :key="i" :post="p" :config="config" :font="wallFont" :now="now" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.sw-ltr {
  animation: sw-ltr linear infinite;
}
.sw-rtl {
  animation: sw-rtl linear infinite;
}
@keyframes sw-ltr {
  to {
    transform: translateX(-50%);
  }
}
@keyframes sw-rtl {
  to {
    transform: translateX(50%);
  }
}
.sw-enter-active,
.sw-leave-active {
  transition: opacity 0.7s ease;
}
.sw-enter-from,
.sw-leave-to {
  opacity: 0;
}
</style>
